// Mail V2 — AI Quick Assist Drawer(右側覆蓋式,回复 / 新建 邮件 共通)。
//
// 2026-07-24 重构:
//   - 删除所有 demo-only props (customerContext / relatedMessages / currentMessage)
//   - 删除对应的 context 卡片(CRM demo, 邮件 context) — 真实 AI context 由后端管理
//   - 新增 mode prop: "reply" | "compose",决定文案 + 顶部提示行
//   - onInsertToReply 重命名为 onInsert(caller 决定写到哪里)
//   - 完全删除 streaming 概念,只接收 aiGeneratedText(state 已经在 caller)
//
// 流式历史(已删):
//   - 流式生成 setInterval 逻辑 → 删除
//   - onStop prop → 删除
//   - 同步 generate() 在 caller 处 await 一次即可。
import React, { useEffect } from "react";
import {
  Sparkles,
  X,
  Loader2,
  Copy,
  Send,
} from "lucide-react";

export type AiDrawerMode = "reply" | "compose";
export type AiDrawerTone = "business" | "polite" | "concise" | "custom";

const TONE_LABELS: Record<AiDrawerTone, string> = {
  business: "ビジネス",
  polite: "丁寧",
  concise: "簡潔",
  custom: "カスタム",
};

const TITLE_BY_MODE: Record<AiDrawerMode, string> = {
  reply: "AI 返信",
  compose: "AI 下書き生成",
};

const MODE_HINT: Record<AiDrawerMode, string> = {
  reply:
    "返信モード — 選択中メールのスレッドを AI が参照します",
  compose:
    "新規メールモード — ユーザー指示を主入力として下書きを生成します",
};

const GENERATE_BTN_LABEL: Record<AiDrawerMode, string> = {
  reply: "AI返信を生成",
  compose: "AI下書きを生成",
};

const INSERT_BTN_LABEL: Record<AiDrawerMode, string> = {
  reply: "返信に挿入",
  compose: "本文に挿入",
};

interface MailV2AiDrawerProps {
  /** mode = reply: AI 生成结果会插入到 Reply pane; compose: 插入到 Compose body */
  mode: AiDrawerMode;
  aiTone: AiDrawerTone;
  onChangeAiTone: (t: AiDrawerTone) => void;
  aiPrompt: string;
  onChangeAiPrompt: (s: string) => void;
  maxPromptLength: number;
  aiGeneratedText: string;
  isAiGenerating: boolean;
  aiErrorMessage?: string | null;
  onGenerate: () => void;
  onRegenerate: () => void;
  /** caller 自己决定把 aiGeneratedText 写到哪里(Reply pane / Compose body)。 */
  onInsert: () => void;
  onCopy: () => void;
  onClose: () => void;
  /**
   * 2026-07-24: 是否渲染 backdrop。
   * - reply 模式(默认 true):半透明 backdrop 覆盖整个 .mv2-body,点 backdrop 关闭。
   * - compose 模式(false):drawer 嵌入 dialog 内,不需要 backdrop(已有 dialog backdrop)。
   */
  showBackdrop?: boolean;
}

const PROMPT_PLACEHOLDER = `例:
・丁寧に謝罪する
・来週火曜日までに対応すると伝える
・追加資料を依頼する`;

export function MailV2AiDrawer({
  mode,
  aiTone,
  onChangeAiTone,
  aiPrompt,
  onChangeAiPrompt,
  maxPromptLength,
  aiGeneratedText,
  isAiGenerating,
  aiErrorMessage,
  onGenerate,
  onRegenerate,
  onInsert,
  onCopy,
  onClose,
  showBackdrop = true,
}: MailV2AiDrawerProps) {
  const tones: AiDrawerTone[] = ["business", "polite", "concise", "custom"];

  // Escape 关闭 Drawer(只在 drawer 开启时生效)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const title = TITLE_BY_MODE[mode];
  const generateLabel = GENERATE_BTN_LABEL[mode];
  const insertLabel = INSERT_BTN_LABEL[mode];

  return (
    <>
      {showBackdrop && (
        <div
          className="ai-drawer-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className="ai-reply-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-mode={mode}
      >
        {/* === Header === */}
        <div className="ai-drawer-header">
          <div className="ai-drawer-title">
            <span className="ai-drawer-title-icon">
              <Sparkles size={18} />
            </span>
            <span>{title}</span>
          </div>
          <button
            type="button"
            className="ai-drawer-close-btn"
            onClick={onClose}
            aria-label="閉じる"
            title="閉じる"
          >
            <X size={18} />
          </button>
        </div>

        {/* === Scroll content === */}
        <div className="ai-drawer-scroll">
          {/* 0. mode 提示行(单行, 让用户明白 AI 在做什么) */}
          <div className="ai-drawer-mode-hint" role="note">
            {MODE_HINT[mode]}
          </div>

          {/* 1. 回复风格 */}
          <div className="ai-drawer-card">
            <div className="ai-drawer-card-label">返信スタイル</div>
            <div className="ai-drawer-tone-group">
              {tones.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`ai-drawer-tone-btn${aiTone === t ? " active" : ""}`}
                  onClick={() => onChangeAiTone(t)}
                  aria-pressed={aiTone === t}
                >
                  {TONE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {/* 2. 用户指示词 */}
          <div className="ai-drawer-card">
            <div className="ai-drawer-card-label">お客様からの指示（任意）</div>
            <textarea
              className="ai-drawer-prompt"
              placeholder={PROMPT_PLACEHOLDER}
              value={aiPrompt}
              onChange={(e) => onChangeAiPrompt(e.target.value)}
              aria-label="お客様からの指示"
            />
            <div className="ai-drawer-char-count">
              {aiPrompt.length} / {maxPromptLength}
            </div>
          </div>

          {/* 3. 生成按钮 / 生成中状态 */}
          <div className="ai-drawer-card">
            {aiErrorMessage && !isAiGenerating && (
              <div
                className="ai-drawer-error"
                role="alert"
                data-testid="ai-drawer-error"
                style={{
                  padding: "8px 10px",
                  background: "#fef2f2",
                  color: "#b91c1c",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                  fontSize: 12,
                  marginBottom: 8,
                }}
              >
                AI 生成失敗: {aiErrorMessage}
              </div>
            )}
            {isAiGenerating ? (
              <div
                data-testid="ai-drawer-loading"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  padding: "12px 16px",
                  background: "#eef4ff",
                  border: "1px solid #c7d2fe",
                  borderRadius: 8,
                  color: "#1d4ed8",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <Loader2
                  size={16}
                  className="ai-drawer-spinner"
                  style={{
                    animation: "ai-drawer-spin 0.9s linear infinite",
                  }}
                />
                AI 生成中... しばらくお待ちください
              </div>
            ) : (
              <button
                type="button"
                className="ai-drawer-generate-btn"
                onClick={onGenerate}
                disabled={isAiGenerating}
                data-testid="ai-drawer-generate"
              >
                <Sparkles size={16} />
                {generateLabel}
              </button>
            )}
          </div>

          {/* 4. 生成结果(只在有输出时显示) */}
          {(aiGeneratedText || isAiGenerating) && (
            <div className="ai-drawer-card">
              <div className="ai-drawer-card-label">生成結果</div>
              <div className="ai-drawer-output">
                {aiGeneratedText}
                {isAiGenerating && <span className="ai-drawer-cursor" />}
              </div>
              {!isAiGenerating && aiGeneratedText && (
                <div className="ai-drawer-output-actions">
                  <button
                    type="button"
                    className="ai-drawer-output-action primary"
                    onClick={onInsert}
                    title={insertLabel}
                    data-testid="ai-drawer-insert"
                  >
                    <Send size={14} />
                    {insertLabel}
                  </button>
                  <button
                    type="button"
                    className="ai-drawer-output-action"
                    onClick={onCopy}
                    title="クリップボードへコピー"
                  >
                    <Copy size={14} />
                    コピー
                  </button>
                  <button
                    type="button"
                    className="ai-drawer-output-action"
                    onClick={onRegenerate}
                    title="再生成"
                  >
                    再生成
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}